import { Button, Switch, Text, TextLink, VSpacer } from '@/components/DesignSystem';
import { QueryKeys } from '@/constants/QueryKeys';
import { Routes } from '@/constants/Routes';
import { useSnackbar } from '@/providers/GlobalSnackbarProvider';
import { PointsAuditApi } from '@/utilities/api/PointsAuditApi';
import { RetailerApi } from '@/utilities/api/RetailerApi';
import { ApiPointsAuditLog, ApiPointsAuditProcess } from '@api/interfaces';
import {
  Box,
  Chip,
  CircularProgress,
  Container,
  MenuItem,
  Pagination,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
} from '@mui/material';
import { PointsAuditEventType, PointsAuditRevertBlock } from '@shared/enums';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from 'react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { AuditEventDetailDialog } from './AuditEventDetailDialog';
import { describeProcessSource } from './describeProcessSource';
import { RevertProcessDialog } from './RevertProcessDialog';

const NO_WRAP = { whiteSpace: 'nowrap' } as const;

const CHANGES_NOTHING: PointsAuditEventType[] = [
  PointsAuditEventType.ImportDiscarded,
  PointsAuditEventType.RecalculationQueued,
  PointsAuditEventType.Revert,
];
const LINK_CHANGES: PointsAuditEventType[] = [
  PointsAuditEventType.AccountsLinked,
  PointsAuditEventType.AccountsUnlinked,
];

const PAGE_LIMIT = 20;

/**
 * Imports, syncs and bulk overrides, each revertible. Recalculations and reverts are hidden unless
 * the admin asks for everything.
 */
export const PointsAuditProcesses = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { openSnackbar } = useSnackbar();
  const [searchParams, setSearchParams] = useSearchParams();

  const retailerId = searchParams.get('retailerId') ?? '';
  const [page, setPage] = useState(0);
  const [showAll, setShowAll] = useState(false);
  const [processToRevert, setProcessToRevert] = useState<ApiPointsAuditProcess>();
  // The detail dialog loads the whole process from any one of its rows.
  const [processEventToOpen, setProcessEventToOpen] = useState<ApiPointsAuditLog>();

  const { data: retailers } = useQuery(
    [QueryKeys.GET_RETAILERS],
    () => RetailerApi.list({ limit: 200 }),
  );

  const { data: processes, isFetching } = useQuery(
    [QueryKeys.GET_POINTS_AUDIT_PROCESSES, retailerId, page, showAll],
    () => PointsAuditApi.listProcesses(retailerId, { limit: PAGE_LIMIT, page, showAll }),
    { enabled: !!retailerId, keepPreviousData: true },
  );

  const onReverted = async (message: string) => {
    setProcessToRevert(undefined);
    openSnackbar(message);
    await queryClient.invalidateQueries(QueryKeys.GET_POINTS_AUDIT_PROCESSES);
    await queryClient.invalidateQueries(QueryKeys.GET_POINTS_AUDIT_LOG);
  };

  /** A process with nothing a revert can undo says why, so it doesn't read as an oversight. */
  const notRevertableReason = ({ summaryEvent }: ApiPointsAuditProcess) => {
    if (summaryEvent.eventType === PointsAuditEventType.SyncCompleted) {
      return t($ => $.pointsAudit.processes.notRevertable.sync);
    }
    if (CHANGES_NOTHING.includes(summaryEvent.eventType)) {
      return t($ => $.pointsAudit.processes.notRevertable.changesNothing);
    }
    if (LINK_CHANGES.includes(summaryEvent.eventType)) {
      return t($ => $.pointsAudit.processes.notRevertable.links);
    }
    return t($ => $.pointsAudit.processes.notRevertable.ownScreen);
  };

  const renderAction = (process: ApiPointsAuditProcess) => {
    if (process.isReverted) {
      return (
        <Stack alignItems="flex-end" data-testid={`reverted-${process.processId}`}>
          <Chip
            color="default"
            label={t($ => $.pointsAudit.processes.reverted)}
            size="small"
          />
          {!!process.revertedAt && (
            <Text category="body-small" sx={NO_WRAP}>
              {t($ => $.pointsAudit.processes.revertedBy, {
                date: process.revertedAt,
                name: process.revertedBy ?? t($ => $.pointsAudit.actors.system),
              })}
            </Text>
          )}
        </Stack>
      );
    }
    const disabledReason = {
      [PointsAuditRevertBlock.Locked]: t($ => $.pointsAudit.revert.locked),
      [PointsAuditRevertBlock.Recalculating]: t($ => $.pointsAudit.revert.recalculating),
    }[process.revertBlock as string];
    if (disabledReason) {
      return (
        <Tooltip title={disabledReason}>
          <span data-testid={`revert-disabled-${process.processId}`}>
            <Button
              disabled
              size="small"
              testID={`revert-button-${process.processId}`}
              variant="outlined"
            >
              {t($ => $.pointsAudit.processes.revert)}
            </Button>
          </span>
        </Tooltip>
      );
    }
    // Opens anyway for a newer change, so the dialog can name it and link to it.
    if (!process.isRevertable && process.revertBlock !== PointsAuditRevertBlock.NewerChange) {
      return (
        <Text category="body-small" testID={`not-revertable-${process.processId}`}>
          {notRevertableReason(process)}
        </Text>
      );
    }
    return (
      <Button
        onClick={() => setProcessToRevert(process)}
        size="small"
        testID={`revert-button-${process.processId}`}
        variant="outlined"
      >
        {t($ => $.pointsAudit.processes.revert)}
      </Button>
    );
  };

  return (
    <Container maxWidth="xl">
      <VSpacer size="7" />
      <Text category="headline-medium">{t($ => $.pointsAudit.processes.title)}</Text>
      <VSpacer size="3" />
      <Text category="body-medium">{t($ => $.pointsAudit.processes.description)}</Text>
      <VSpacer size="6" />

      <Stack alignItems="center" direction="row" gap="16px">
        <TextField
          data-testid="points-audit-processes-retailer-select"
          label={t($ => $.pointsAudit.filters.retailer)}
          onChange={(event) => {
            setPage(0);
            setSearchParams(event.target.value ? { retailerId: event.target.value } : {});
          }}
          select
          sx={{ minWidth: 280 }}
          value={retailerId}
        >
          <MenuItem value="">{t($ => $.pointsAudit.filters.selectRetailer)}</MenuItem>
          {retailers?.data.map((retailer) => (
            <MenuItem key={retailer.id} value={retailer.id}>{retailer.name}</MenuItem>
          ))}
        </TextField>
        <Switch
          checked={showAll}
          onChangeChecked={(checked) => {
            setPage(0);
            setShowAll(checked);
          }}
          testID="points-audit-processes-show-all"
        >
          {t($ => $.pointsAudit.processes.showAll)}
        </Switch>
        <Box flexGrow={1} />
        <Link to={`${Routes.ADMIN_POINTS_AUDIT}?retailerId=${retailerId}`}>
          <Text category="body-medium">{t($ => $.pointsAudit.processes.viewTrail)}</Text>
        </Link>
      </Stack>

      <VSpacer size="6" />

      {!retailerId && (
        <Text category="body-medium">{t($ => $.pointsAudit.filters.selectRetailer)}</Text>
      )}

      {!!retailerId && (
        <TableContainer component={Paper}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell width={170}>{t($ => $.pointsAudit.processes.when)}</TableCell>
                <TableCell width={110}>{t($ => $.pointsAudit.processes.type)}</TableCell>
                <TableCell>{t($ => $.pointsAudit.processes.source)}</TableCell>
                <TableCell width={150}>{t($ => $.pointsAudit.processes.actor)}</TableCell>
                <TableCell width={110}>{t($ => $.pointsAudit.processes.farmers)}</TableCell>
                <TableCell width={100}>{t($ => $.pointsAudit.processes.changes)}</TableCell>
                <TableCell align="right" width={160}>
                  {t($ => $.pointsAudit.processes.action)}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isFetching && !processes && (
                <TableRow>
                  <TableCell align="center" colSpan={7}>
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              )}
              {processes?.data.length === 0 && (
                <TableRow>
                  <TableCell align="center" colSpan={7}>
                    {t($ => $.pointsAudit.processes.empty)}
                  </TableCell>
                </TableRow>
              )}
              {processes?.data.map((process) => (
                <TableRow
                  data-testid={`points-audit-process-${process.processId}`}
                  key={process.processId}
                >
                  <TableCell sx={NO_WRAP}>
                    {t($ => $.pointsAudit.dateTime, { date: process.lastEventAt })}
                  </TableCell>
                  <TableCell>
                    <Chip label={process.processType} size="small" />
                  </TableCell>
                  <TableCell>
                    <TextLink
                      onClick={() => setProcessEventToOpen(process.summaryEvent)}
                      testID={`process-source-${process.processId}`}
                    >
                      {describeProcessSource(process, t)}
                    </TextLink>
                  </TableCell>
                  <TableCell>
                    {process.actorName ?? t($ => $.pointsAudit.actors.system)}
                  </TableCell>
                  <TableCell>{process.farmersAffected}</TableCell>
                  <TableCell>{process.ledgerRows ?? process.eventCount}</TableCell>
                  <TableCell align="right">{renderAction(process)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <VSpacer size="4" />
      {!!processes && processes.total > PAGE_LIMIT && (
        <Stack alignItems="center">
          <Pagination
            count={Math.ceil(processes.total / PAGE_LIMIT)}
            onChange={(_, next) => setPage(next - 1)}
            page={page + 1}
          />
        </Stack>
      )}

      {!!processEventToOpen && (
        <AuditEventDetailDialog
          event={processEventToOpen}
          onClose={() => setProcessEventToOpen(undefined)}
        />
      )}

      {!!processToRevert && (
        <RevertProcessDialog
          key={processToRevert.processId}
          onClose={() => setProcessToRevert(undefined)}
          onOpenProcess={setProcessToRevert}
          onReverted={onReverted}
          process={processToRevert}
        />
      )}
      <VSpacer size="10" />
    </Container>
  );
};
